import { rem } from 'polished';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import * as yup from 'yup';

import { useABLincoln } from '@vimeo/ablincoln';
import { Box } from '@vimeo/bokeh';
import { useField, useForm } from '@vimeo/forms';
import { LoginIdentityUserResponse } from '@vimeo/google-identity-platform';
import { translate } from '@vimeo/i18n-runtime';
import { slate, black } from '@vimeo/iris/color';
import { Checkbox, Notice } from '@vimeo/iris/components';
import { Paragraph, Link } from '@vimeo/iris/typography';
import { useTurnstile } from '@vimeo/turnstile';

import { InputWrapper, Label, StyledInput } from '../../../InputPlaceholderLabel';
import { trackForgotPasswordClick, trackLogin, trackLoginWithEmailClick } from '../../tracking';
import { Login } from './RequestLogin';
import { ContinueButton, FormSection, Input } from './Shared';

const validationSchema = yup.object({
  email: yup
    .string()
    .email(translate('Please enter a valid email address'))
    .required(translate('Please enter a valid email address')),
  password: yup.string().required(translate('Please enter your password')),
  isOtp: yup.boolean(),
  otp: yup.string().when('isOtp', {
    is: (val) => val === true,
    then: yup
      .string()
      .required(translate('Invalid code, please try again'))
      .matches(/^\d+$/, translate('Invalid code, please try again')),
    otherwise: yup.string().nullable(true),
  }),
});

interface Props {
  xsrft: string;
  redirectUrl?: string;
  email?: string;
  disableEmail?: boolean;
  fa?: {
    container: string;
    thirdPartyIntegration?: string;
  };
  onSuccess?: (response: any) => void;
  onEmailChange?: (email: string) => void;
  target?: string;
  showEnterpriseOptInConsent?: boolean;
  placeholder?: {
    email: string;
    name?: string;
    password: string;
  };
  className?: string;
  submitCtaText?: string;
  isF2PVariant?: boolean;
  isTwoStep?: boolean;
  onTurnstileTokenChange?: (token: string) => void;
  isFullScreen?: boolean;
}
interface EmailForm {
  email: string;
  password: string;
  otp: string;
  isOtp: boolean;
  turnstileToken: string;
}

const CheckboxContainer = styled.div`
  margin-top: ${rem(8)};
`;

export function LoginEmailForm({
  xsrft,
  redirectUrl,
  email: email_,
  disableEmail,
  fa,
  onSuccess,
  onEmailChange,
  target = '_self',
  showEnterpriseOptInConsent,
  placeholder,
  className,
  submitCtaText = translate('Continue'),
  isTwoStep,
  onTurnstileTokenChange,
  isFullScreen,
}: Props) {
  const [error, setError] = useState<string | null>(null);
  const [showOtpError, toggleOtpError] = useState<boolean>(false);
  const [showOtpField, setShowOtpField] = useState<boolean>(false);
  const [otpType, setOtpType] = useState<string>('');
  const [enterpriseOptInAccepted, setEnterpriseOptInAccepted] = useState(false);
  const [captchaVisible, setCaptchaVisible] = useState(false);
  const [hasRequiredEmailVerification, setHasRequiredEmailVerification] = useState(false);

  const turnstileErrorCallback = () => {
    setCaptchaVisible(true);
    setError(
      translate({
        singular:
          'Whoops! We are having some technical difficulties. To continue, contact our {A}support team{/A}.',
        replacements: {
          A: (children: string) => (
            <Link href="https://vimeo.com/help/contact" target="_blank">
              {children}
            </Link>
          ),
        },
      }),
    );
  };

  const { turnstileToken } = useTurnstile(false, 'turnstile-container', {
    errorCb: turnstileErrorCallback,
    beforeInteractiveCb: () => setCaptchaVisible(true),
  });

  const isInGIPTest =
    useABLincoln()('JS.TrustandSafety.GIPMigration', 'in_GIP', 'control') === 'variant';

  const handleOnSubmit = async (values: EmailForm, requestNewToken?: boolean) => {
    showOtpError && toggleOtpError(false);
    error && setError('');
    if (!emailForm.valid && !requestNewToken) {
      setError(translate('Please enter your email and password'));
      return;
    }

    let data =
      `email=${encodeURIComponent(values.email)}&` +
      `password=${encodeURIComponent(values.password)}&` +
      `token=${xsrft}&` +
      `otp=${encodeURIComponent(values.otp)}&` +
      `action=login&` +
      `service=${isInGIPTest ? 'gip' : 'vimeo'}&` +
      `otp_required=${encodeURIComponent(otpType)}&` +
      `request_new_token=${encodeURIComponent(requestNewToken ? 1 : 0)}&` +
      `enterprise_opt_in_consent=${encodeURIComponent(enterpriseOptInAccepted ? 1 : 0)}&` +
      `turnstile_token=${turnstileToken || ''}`;

    /*
     * CI Branch Fix: This `redirect` query param is needed for, similar check is already
     * added for backend driven login screen, check AuthGrantController::buildAuthFormRedirectParams
     * for more details.
     */
    if (redirectUrl) {
      data += `&redirect=${encodeURIComponent(redirectUrl)}`;
    }

    if (isInGIPTest) {
      const { GoogleIdentityPlatform, LOGIN_ERROR_CODES, USER_DISABLED_ERROR_CODE } = await import(
        '@vimeo/google-identity-platform'
      );
      const googleIdentityPlatform = new GoogleIdentityPlatform();
      try {
        if (values.email && values.password) {
          let response = await googleIdentityPlatform.signIn(values.email, values.password);
          response = await googleIdentityPlatform.loginGoogleIdentityUser(
            xsrft,
            values.email,
            response.user.accessToken,
          );
          if (onSuccess) {
            onSuccess(response);
          }
        }
      } catch (error) {
        const errorCode = error.code;
        if (USER_DISABLED_ERROR_CODE === errorCode) {
          const response: LoginIdentityUserResponse =
            await googleIdentityPlatform.loginGoogleIdentityUser(xsrft, values.email, '');
          if (response?.force_redirect && response?.redirect_url) {
            window.location.href = response?.redirect_url;
          }
        } else if (LOGIN_ERROR_CODES.includes(errorCode)) {
          setError(translate('Email and password do not match'));
        }
      }
    } else {
      try {
        const response = await Login(data, '/log_in');
        // Check if we've exceeded rateliimiting
        if (response.status === 429) {
          setError(
            translate(
              'Sorry, but you have had too many invalid login attempts. Please wait a few minutes and try again.',
            ),
          );
          return;
        }
        if (response.force_redirect && response.redirect_url) {
          window.location.href = response.redirect_url;
        }
        if (onSuccess) {
          onSuccess(response);
        }
      } catch (errors) {
        let msg = 'Email and password do not match';
        for (const key in errors) {
          // Ignore errors with 'has_error_' prefix. These are used for client-side
          // error handling and do not point to actual error strings.
          if (errors.hasOwnProperty(key) && key.indexOf('has_error_') !== 0) {
            msg = errors[key];
            break;
          }
        }

        if (errors.hasOwnProperty('email')) {
          setError(translate('Please enter a valid email address'));
          return;
        }
        if (errors.hasOwnProperty('has_error_invalid_credentials')) {
          setError(translate('Email and password do not match'));
          return;
        }
        if (errors.hasOwnProperty('has_error_resent_otp') && requestNewToken) {
          setError(translate('Authentication code emailed'));
          return;
        }
        if (errors.hasOwnProperty('has_error_email_verification_required')) {
          setHasRequiredEmailVerification(true);
          setError(
            translate({
              singular: 'To finish signing up, follow the instructions we sent to: {EMAIL}',
              replacements: { EMAIL: values.email },
            }),
          );
          return;
        }
        let showOtp = false;
        for (const key in errors) {
          if (errors.hasOwnProperty(key) && key.indexOf('has_error_invalid_otp') === 0) {
            showOtp = true;
            break;
          }
        }

        if (showOtp) {
          if (errors.hasOwnProperty('has_error_invalid_otp_email')) {
            setOtpType('email');
          } else if (errors.hasOwnProperty('has_error_invalid_otp_totp')) {
            setOtpType('totp');
          } else if (errors.hasOwnProperty('has_error_invalid_otp_risky')) {
            setOtpType('risky');
          }
          if (otp?.iris?.value) {
            toggleOtpError(true);
          }
        }
        values.isOtp = showOtp;
        setShowOtpField(showOtp);

        if (errors.hasOwnProperty('has_captcha_error')) {
          msg = translate({
            singular:
              'Unable to verify CAPTCHA. Please try again or visit the {A}Support Center{/A} for help.',
            replacements: {
              A: (children: string) => (
                <Link href="https://vimeo.com/help/contact" target="_blank">
                  {children}
                </Link>
              ),
            },
          });
          setShowOtpField(false);
          setOtpType('');
          setError(msg);
        }
      }
    }
  };
  const emailForm = useForm({
    validationSchema,
    initialValues: {
      email: email_ || '',
      password: '',
      otp: '',
      isOtp: false,
      enterpriseOptInAccepted: false,
      turnstileToken: '',
    },
    onSubmit: (values) => handleOnSubmit(values),
  });

  const email = useField(emailForm, 'email');
  const password = useField(emailForm, 'password');
  const otp = useField(emailForm, 'otp');
  const turnstileTokenField = useField(emailForm, 'turnstileToken');
  const onChangeEmailField = (changeEvent: React.ChangeEvent<HTMLInputElement>) => {
    email.iris.onChange(changeEvent);
    onEmailChange && onEmailChange(changeEvent.target.value);
  };

  const handleOtpOnChange = (changeEvent: React.ChangeEvent<HTMLInputElement>) => {
    showOtpError && toggleOtpError(false);
    otp.iris.onChange(changeEvent);
  };

  const inputEmailText = {
    placeholder: placeholder ? placeholder.email : translate('Email address'),
  };
  const inputPasswordText = {
    placeholder: placeholder ? placeholder.password : translate('Password'),
  };

  useEffect(() => {
    if (onTurnstileTokenChange) onTurnstileTokenChange(turnstileToken);
    turnstileTokenField?.handlers?.setValue(turnstileToken);
  }, [turnstileToken, turnstileTokenField?.handlers, onTurnstileTokenChange]);

  const getInputFields = () =>
    isFullScreen ? (
      <>
        <InputWrapper formFieldErrorMessage={error} isInvalid={error ? true : false}>
          <StyledInput
            data-testid="site_login_email_input"
            id="email_login"
            isDisabled={disableEmail || !!otpType}
            {...email.iris}
            onChange={onChangeEmailField}
            marginBottom="200"
          />
          <Label>{translate('Enter email')}</Label>
        </InputWrapper>
        <InputWrapper formFieldErrorMessage={error} isInvalid={error ? true : false}>
          <StyledInput
            data-testid="site_login_password_input"
            id="password_login"
            type="password"
            isDisabled={!!otpType}
            {...password.iris}
          />
          <Label>{translate('Enter password')}</Label>
        </InputWrapper>
      </>
    ) : (
      <>
        <Input
          {...inputEmailText}
          data-testid="site_login_email_input"
          id="email_login"
          status={error ? 'negative' : 'neutral'}
          disabled={disableEmail || !!otpType}
          {...email.iris}
          onChange={onChangeEmailField}
        />
        {/* #iris TODO convert to iris password input  */}
        <Input
          data-testid="site_login_password_input"
          id="password_login"
          {...inputPasswordText}
          type="password"
          status={error ? 'negative' : 'neutral'}
          disabled={!!otpType}
          {...password.iris}
        />
      </>
    );

  return (
    <form onSubmit={emailForm.handleSubmit} noValidate>
      {getInputFields()}
      {!isFullScreen ? (
        <FormSection>
          <Link
            format="primary"
            href="/forgot_password"
            variant="minimal"
            target={target}
            onClick={() => trackForgotPasswordClick(isTwoStep, redirectUrl)}
          >
            <ParagraphLink size="2" className={className}>
              {translate('Forgot your password?')}
            </ParagraphLink>
          </Link>
        </FormSection>
      ) : null}
      {showEnterpriseOptInConsent ? (
        <FormSection>
          <CheckboxContainer>
            <Checkbox
              checked={enterpriseOptInAccepted}
              onChange={() => {
                setEnterpriseOptInAccepted(!enterpriseOptInAccepted);
              }}
              id="enterprise-opt-in-consent"
              name="enterprise-opt-in-consent"
              label={
                <Paragraph size="3">
                  {translate(
                    'By signing into a Vimeo Enterprise account, I agree and understand that the Vimeo Enterprise account owner will administer my account and have access to my account information, including viewing activity. To withdraw from ongoing disclosures of your viewing activity to the account owner, contact your administrator.',
                  )}
                </Paragraph>
              }
            />
          </CheckboxContainer>
        </FormSection>
      ) : null}
      {otpType === 'email' && (
        <FormSection>
          <Notice format="primary">
            <Paragraph size="3">
              {translate('A security code has been sent to your email address')}
            </Paragraph>
          </Notice>
        </FormSection>
      )}
      {otpType === 'totp' && (
        <FormSection>
          <Notice format="primary">
            <Paragraph size="3">
              {translate(
                'Please enter the code from the authenticator app you configured when setting up Two-Factor authentication (2FA).',
              )}
            </Paragraph>
          </Notice>
        </FormSection>
      )}
      {otpType === 'risky_otp' && (
        <FormSection>
          <Notice format="primary">
            <Paragraph size="3">
              {translate({
                singular:
                  "As an added security measure, we've sent a 6-digit passcode to your email address. Please enter it below. {A}Need help?{/A}",
                replacements: {
                  A: (text: string) => (
                    <a href="https://vimeo.com/help/contact?category=cf_account&subcategory=cf_risky_2fa_challenge">
                      {text}
                    </a>
                  ),
                },
              })}
            </Paragraph>
          </Notice>
        </FormSection>
      )}
      {error && (
        <FormSection>
          <Notice format={hasRequiredEmailVerification ? 'primary' : 'negative'}>
            <Paragraph size="3">
              {translate({
                singular: '{ERROR}',
                replacements: { ERROR: error },
              })}
            </Paragraph>
          </Notice>
        </FormSection>
      )}
      <Box as="section" mx="auto" my={rem(10)} display={captchaVisible ? 'block' : 'none'}>
        <div id="turnstile-container"></div>
      </Box>
      {showOtpField && (
        <>
          <Input
            {...otp.iris}
            id="otp"
            placeholder={translate('Security code')}
            type="text"
            status={error ? 'negative' : 'neutral'}
            onChange={handleOtpOnChange}
          />
          <FormSection>
            <EmailOtp
              onClick={() => {
                handleOnSubmit(
                  {
                    email: email.iris.value,
                    password: password.iris.value,
                    otp: otp.iris.value,
                    isOtp: true,
                  } as EmailForm,
                  true,
                );
              }}
            >
              {translate('Email me a new code')}
            </EmailOtp>
          </FormSection>
        </>
      )}

      {showOtpError && (
        <FormSection>
          <Notice format="negative">
            <Paragraph size="3">{translate('Security Code entered is incorrect')}</Paragraph>
          </Notice>
        </FormSection>
      )}

      <FormSection>
        <ContinueButton
          data-testid="site_login_submit_button"
          disabled={!emailForm.valid || (showEnterpriseOptInConsent && !enterpriseOptInAccepted)}
          loading={emailForm.submitting}
          onClick={() => {
            trackLogin('password', fa);
            trackLoginWithEmailClick(isTwoStep, redirectUrl);
          }}
          className={className}
        >
          {submitCtaText}
        </ContinueButton>
      </FormSection>
    </form>
  );
}

const ParagraphLink = styled(Paragraph)`
  color: ${slate(500)};
  &.f2pPilotRegLogin {
    text-decoration: underline;
    color: ${black};
  }
  &:hover {
    text-decoration: underline;
  }
`;

const EmailOtp = styled.div`
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 14px;
  margin-top: 5px;
  display: block;
  text-decoration: none;
`;
